// MyProfile — donor portal profile-edit page (CHG-1494 / ITER-3612).
//
// β.5 of the donor-portal program plan
// (/tmp/donor-portal-program.md). Consumes GET + PATCH
// /api/v1/me/profile (CHG-1478 α.1).
//
// Uses the canonical SettingsPage + SettingsForm primitives (CHG-1239)
// rather than hand-rolling a form, so the page picks up shell chrome,
// dirty-tracking, save semantics, and the field-primitive registry
// (EmailField / PhoneField / LocalePicker / TimezonePicker) for free.
//
// Single-section section vocabulary (no rail; SettingsForm renders the
// section directly). intent_topics is intentionally absent — it's
// persisted via α.6 /me/preferences and surfaces on MyPreferences.tsx (β.6).
//
// CHG-2942 (Slice C, DP-R1/DP-R2): phone is READ-ONLY here — the runtime
// rejects phone_number on PATCH /my-giving/me (extra="forbid", CHG-2891).
// Phone changes run through the two-step OTP ContactChangeDialog against
// POST /my-giving/me/request-phone-change + /confirm-phone-change. The
// display prefers canonical phone_e164 (CHG-2939 §8 decision 2) and falls
// back to the deprecated phone_number alias. Email self-change is APPROVED
// (CHG-2933 §8 decision 1) but its portal routes are owned by Slice D and
// not yet deployed — email stays read-only with approved-pending copy
// until the C2 follow-up wires the email variant of the dialog.

import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SettingsPage } from "../components/composition";
import { ContactChangeDialog } from "../components/donor/ContactChangeDialog";
import type { SettingsSection } from "../lib/settings-sections";
import {
  fetchMeProfile,
  patchMeProfile,
  type MeProfile,
  type MeProfilePatch,
} from "../api/me-profile";

const SECTION_ID = "profile";

// Single section, single field group — the SettingsPage primitive
// gracefully renders a single-section vocabulary (the section rail
// still renders the one entry; we keep it for layout consistency
// with /settings).
const MY_PROFILE_SECTION: SettingsSection = {
  section_id: SECTION_ID,
  label: "Profile",
  icon: "user",
  description: "Identity + contact info shared across all the organizations you give to.",
  route: "?section=profile",
  field_groups: [
    {
      group_id: "identity",
      label: "Identity",
      fields: [
        {
          field_id: "full_name",
          label: "Full name",
          field_type: "text",
          pending_primitive: true,
          required: true,
          max_length: 255,
          hint: "Shown on your receipts and on the donor wall (unless your display preference is anonymous).",
        },
        {
          field_id: "email",
          label: "Email",
          field_type: "email",
          readonly: true,
          // CHG-2942 — accurate approved-pending copy (replaces "coming
          // soon"): self-service email change is APPROVED (CHG-2933 §8
          // decision 1); the verified-code flow ships once the portal
          // email-change routes land (Slice D → C2 follow-up).
          hint: "Self-service email change with a verification code is on the way. Until it ships, contact support to change your email.",
        },
        {
          field_id: "phone",
          label: "Phone number",
          field_type: "phone",
          readonly: true,
          // CHG-2942 — read-only display + OTP dialog (DP-R1). The PATCH
          // endpoint rejects phone_number; Change runs the verified flow.
          action: { action_id: "change-phone", label: "Change" },
          hint: "Used for transactional alerts (receipts, recurring-gift reminders). Use Change to update it — we'll text a verification code to the new number.",
        },
      ],
    },
    {
      group_id: "localization",
      label: "Localization",
      fields: [
        {
          field_id: "preferred_locale",
          label: "Locale",
          field_type: "locale_picker",
          hint: "BCP-47 code (e.g. en-US, fr-CA). Drives currency + date formatting on receipts and the donor portal.",
        },
        {
          field_id: "preferred_timezone",
          label: "Timezone",
          field_type: "timezone_picker",
          hint: "IANA name (e.g. America/New_York). Drives donation timestamps shown in your portal.",
        },
      ],
    },
  ],
};

// CHG-1513 portal-pivot: communication-channel section removed. The
// shim's preferred_communication_channel field isn't in portal.py's
// AccountUpdate schema. Channel opt-in toggles live on /preferences
// (DonorCommunicationPreferences) which MyPreferences.tsx renders.

function meProfileToValues(p: MeProfile): Record<string, unknown> {
  return {
    full_name: p.full_name,
    email: p.email ?? "",
    // CHG-2942 / §8 decision 2 — prefer the canonical verified phone;
    // fall back to the deprecated alias (legacy raw value when no
    // verified phone exists yet).
    phone: p.phone_e164 ?? p.phone_number ?? "",
    preferred_locale: p.preferred_locale,
    preferred_timezone: p.preferred_timezone,
  };
}

function buildPatch(
  server: Record<string, unknown>,
  edits: Record<string, unknown>,
): MeProfilePatch {
  const patch: MeProfilePatch = {};
  // Whitelist of writable fields — email and phone are read-only at this
  // surface (CHG-2942: phone_number on PATCH /my-giving/me is rejected 422
  // by the runtime; phone changes go through the OTP dialog).
  const writable = [
    "full_name",
    "preferred_locale",
    "preferred_timezone",
  ] as const;
  for (const key of writable) {
    if (!(key in edits)) continue;
    const edited = edits[key];
    if (!Object.is(edited, server[key])) {
      // Send the edited value (including null) so the user can clear
      // an optional field; full_name's min_length=1 guard on the
      // backend rejects empty values.
      (patch as Record<string, unknown>)[key] = edited;
    }
  }
  return patch;
}

export function MyProfile() {
  const queryClient = useQueryClient();
  const { data, isLoading, error: fetchError } = useQuery({
    queryKey: ["me", "profile"],
    queryFn: fetchMeProfile,
  });

  const [edits, setEdits] = useState<Record<string, unknown>>({});
  // CHG-2942 — OTP contact-change dialog state.
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);

  const serverValues = useMemo<Record<string, unknown>>(
    () => (data ? meProfileToValues(data) : {}),
    [data],
  );
  const values = useMemo<Record<string, unknown>>(
    () => ({ ...serverValues, ...edits }),
    [serverValues, edits],
  );
  const dirty = useMemo(() => {
    return Object.entries(edits).some(([k, v]) => !Object.is(v, serverValues[k]));
  }, [edits, serverValues]);

  const mutation = useMutation({
    mutationFn: (patch: MeProfilePatch) => patchMeProfile(patch),
    onSuccess: () => {
      setEdits({});
      queryClient.invalidateQueries({ queryKey: ["me", "profile"] });
    },
  });

  const onChange = (_sectionId: string, fieldId: string, value: unknown) => {
    if (fieldId === "email" || fieldId === "phone") return; // read-only at this surface
    setEdits((prev) => ({ ...prev, [fieldId]: value }));
  };

  const onSaveSection = async (_sectionId: string) => {
    const patch = buildPatch(serverValues, edits);
    if (Object.keys(patch).length === 0) return;
    // Fire-and-forget: React Query captures errors into mutation.error
    // which we surface via the SettingsPage `error` prop. Using mutate
    // (not mutateAsync) keeps the rejection out of the unhandled-promise
    // path — the same pattern that useSettings.ts adopts for /settings.
    mutation.mutate(patch);
  };

  const onFieldAction = (_sectionId: string, _fieldId: string, actionId: string) => {
    if (actionId === "change-phone") setPhoneDialogOpen(true);
  };

  const error =
    mutation.error
      ? extractErrorMessage(mutation.error)
      : fetchError
      ? extractErrorMessage(fetchError)
      : undefined;

  return (
    <>
      <SettingsPage
        title="My profile"
        sections={[MY_PROFILE_SECTION]}
        values={{ [SECTION_ID]: values }}
        onChange={onChange}
        onSaveSection={onSaveSection}
        onFieldAction={onFieldAction}
        dirty={{ [SECTION_ID]: dirty }}
        busy={isLoading || mutation.isPending}
        error={error}
      />
      <ContactChangeDialog
        kind="phone"
        open={phoneDialogOpen}
        onClose={() => setPhoneDialogOpen(false)}
        onSuccess={() =>
          // Refetch so the read-only display shows the new verified phone
          // (the confirm response + re-fetch both carry phone_e164 — CHG-2939).
          queryClient.invalidateQueries({ queryKey: ["me", "profile"] })
        }
      />
    </>
  );
}

function extractErrorMessage(err: unknown): string {
  if (!err) return "";
  const e = err as {
    response?: { data?: { detail?: unknown; message?: unknown } };
    message?: string;
  };
  // CHG-2942 fix pass — AppException bodies carry the copy in `message`
  // ({error, message, details, payload} — core/exceptions.py); `detail` is
  // the FastAPI HTTPException / Pydantic-422 fallback (same hardening as
  // ContactChangeDialog.describeContactChangeError).
  const raw = e?.response?.data?.message ?? e?.response?.data?.detail;
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0] as { msg?: string };
    if (first?.msg) return first.msg;
  }
  return e?.message ?? "Something went wrong saving your profile.";
}

// CHG-1513 portal-pivot — PreferredCommunicationChannel re-export removed
// (preferred_communication_channel field dropped; channel opt-ins live on
// /preferences via DonorCommunicationPreferences instead).
